<template>
  <main class="main home">
    <section
      class="hero"
      :style="{ backgroundImage: `url(${ $i18n.translate(hero.image)})` }"
    >
      <div class="wrapper hero__wrapper">
        <h1
          v-if="hero.title.length > 0"
          class="hero__title"
        >
          <span
            v-for="(line, i) in hero.title"
            :key="`title_${i}`"
            v-html="$i18n.translate(line.line)"
          />
        </h1>

        <p
          v-if="hero.date && hero.date[$i18n.locale]"
          class="hero__date"
        >
          <span>{{ $i18n.translate(hero.date) }}</span>
        </p>

        <div class="hero__row">
          <app-link
            :link="hero.facebook"
            class="hero__fb"
            v-slot="{ text }"
          >
            <fa-icon
              class="margin__r--6"
              :icon="['fab', 'facebook-square']"
            />

            {{ text }}
          </app-link>

          <div
            class="organisers"
            v-if="organisers.logos && organisers.logos.length > 0"
          >
            <h4
              v-if="organisers.title"
              class="organisers__title"
            >
              {{ $i18n.translate(organisers.title) }}
            </h4>

            <ul class="organisers__logos">
              <li
                v-for="(organiser, i) in organisers.logos"
                :key="`organisers_${i}`"
                class="organiser"
              >
                <app-link
                  :link="organiser.link"
                  class="organiser__link"
                  v-slot="{ text }"
                  v-if="organiser.link"
                >
                  <img
                    :src="$i18n.translate(organiser.logo)"
                    :alt="text"
                    class="organiser__logo"
                  >
                </app-link>

                <img
                  :src="$i18n.translate(organiser.logo)"
                  :alt="organiser.link.text"
                  class="organiser__logo"
                  v-else
                >
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section
      class="stats"
      scroll-animation
    >
      <div class="wrapper">
        <app-counter
          :metrics="metrics"
          :query="{ campaign_id: stats.campaign_id || [] }"
        />
      </div>
    </section>

    <section
      class="sports"
      v-if="sports.length > 0"
      scroll-animation
    >
      <div class="wrapper">
        <ul
          class="sports__mobile"
        >
          <li
            v-for="(sport, i) in sports"
            :key="`sports_mobile_${i}`"
            class="sports__mobile-item"
          >
            <figure
              v-if="$i18n.translate(sport.icon)"
              class="margin__b--12"
              v-html="icons[$i18n.translate(sport.icon)]"
            />

            {{ $i18n.translate(sport.name) }}
          </li>
        </ul>

        <ul
          class="sports__names"
        >
          <li
            v-for="(sport, i) in sports"
            :key="`sports_name_${i}`"
            class="sports__name"
          >
            {{ $i18n.translate(sport.name) }}
          </li>
        </ul>

        <ul
          class="sports__icons"
        >
          <li
            v-for="(sport, i) in sports"
            :key="`sports_icon_${i}`"
            class="sports__icon"
          >
            <figure
              v-if="$i18n.translate(sport.icon)"
              v-html="icons[$i18n.translate(sport.icon)]"
            />
          </li>
        </ul>
      </div>
    </section>

    <section
      class="live"
      v-if="live.url && $i18n.translate(live.url)"
      scroll-animation
    >
      <div class="wrapper">
        <h2
          class="live__title"
          v-if="live.title && $i18n.translate(live.title)"
        >
          {{ $i18n.translate(live.title) }}
        </h2>


        <app-video-embed
          :src=" $i18n.translate(live.url)"
          class="live__video"
        />
      </div>
    </section>

    <section
      class="about"
      scroll-animation
    >
      <div class="wrapper about__wrapper">
        <article class="about__content">
          <ul v-if="about.content && about.content.length > 0">
            <li
              v-for="(line, i) in about.content"
              :key="`about_content_${i}`"
              v-html="toDOM($i18n.translate(line.text))"
            />
          </ul>
        </article>

        <div
          class="about__slider"
          ref="slider"
        >
          <div
            class="about__slider-slides"
            ref="slides"
            v-if="about.images && about.images.length > 0"
            :style="slidesStyle"
            :class="{ 'is-animated': has_transition }"
          >
            <figure
              class="about__slider-slide"
              v-for="(slide, i) in clonedSlides"
              :key="`slider_slide_${i}`"
              :style="{ backgroundImage: `url(${$i18n.translate(slide.image)})` }"
            >
              <fa-icon
                :icon="['far', 'image']"
                size="6x"
                v-if="!$i18n.translate(slide.image)"
              />
            </figure>
          </div>

          <nav
            v-if="about.images && about.images.length > 1"
            class="about__slider-nav"
          >
            <button
              @click="goToPrev()"
              class="about__slider-btn about__slider-btn--prev"
            >
              <svg
                enable-background="new 0 0 58 25"
                version="1.1"
                viewBox="0 0 58 25"
                xml:space="preserve"
                xmlns="http://www.w3.org/2000/svg"
              >

                <path
                  d="m0.5 11.2 10.8-10.7c0.3-0.3 0.7-0.5 1.2-0.5 0.4 0 0.9 0.2 1.2 0.5l1 1c0.3 0.3 0.5 0.7 0.5 1.1s-0.2 0.9-0.5 1.2l-6.4 6.2h48.1c0.9 0 1.6 0.7 1.6 1.6v1.4c0 0.9-0.7 1.7-1.6 1.7h-48.2l6.4 6.3c0.3 0.3 0.5 0.7 0.5 1.1s-0.2 0.8-0.5 1.1l-1 1c-0.3 0.3-0.7 0.5-1.2 0.5-0.4 0-0.9-0.2-1.2-0.5l-10.7-10.7c-0.3-0.3-0.5-0.7-0.5-1.1 0-0.5 0.2-0.9 0.5-1.2z"
                  clip-rule="evenodd"
                  fill="#fff"
                  fill-rule="evenodd"
                />
              </svg>
            </button>
            <button
              @click="goToNext()"
              class="about__slider-btn about__slider-btn--next"
            >
              <svg
                enable-background="new 0 0 58 25"
                version="1.1"
                viewBox="0 0 58 25"
                xml:space="preserve"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="m57.5 11.2-10.8-10.7c-0.3-0.3-0.7-0.5-1.2-0.5-0.4 0-0.9 0.2-1.2 0.5l-1 1c-0.3 0.3-0.5 0.7-0.5 1.1s0.2 0.9 0.5 1.2l6.3 6.3h-48c-0.9-0.1-1.6 0.6-1.6 1.5v1.4c0 0.9 0.7 1.7 1.6 1.7h48.2l-6.4 6.3c-0.3 0.3-0.5 0.7-0.5 1.1s0.2 0.8 0.5 1.1l1 1c0.3 0.3 0.7 0.5 1.2 0.5 0.4 0 0.9-0.2 1.2-0.5l10.8-10.7c0.3-0.3 0.5-0.7 0.5-1.2-0.1-0.4-0.3-0.8-0.6-1.1z"
                  clip-rule="evenodd"
                  fill="#fff"
                  fill-rule="evenodd"
                />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    </section>

    <section
      class="steps"
      scroll-animation
      v-if="steps.content && steps.content.length > 0"
    >
      <div class="wrapper steps__wrapper">
        <article
          class="steps__item step"
          v-for="(step, i) in steps.content"
          :key="`step_${i}`"
        >
          <h3
            v-if="$i18n.translate(step.title)"
            class="step__title"
          >
            {{ $i18n.translate(step.title) }}
          </h3>

          <app-content
            class="margin__t--24"
            :content="$i18n.translate(step.content)"
          />
        </article>
      </div>

      <div
        class="wrapper steps__footer"
        v-if="steps.cta && steps.cta.show && $i18n.translate(steps.cta.text)"
      >
        <app-link
          :link="steps.cta"
          class="cta"
        />
      </div>
    </section>

    <section
      class="projects"
      scroll-animation
    >
      <div
        class="wrapper"
        v-if="project_cards.length > 0"
      >
        <h2
          class="projects__title margin__b--48"
          v-if="projects.title && $i18n.translate(projects.title)"
        >
          {{ $i18n.translate(projects.title) }}
        </h2>


        <div class="projects__list row margin__b--36">
          <template
            v-for="project in project_cards"
            :key="project.id"
          >
            <section class="col-12 col-sm-6 col-md-3">
              <project-card
                class="project-card"
                :project="project"
              />
            </section>
          </template>
        </div>

        <div
          class="projects__footer margin__t--48"
          v-if="projects.cta && projects.cta.show && $i18n.translate(projects.cta.text)"
        >
          <app-link
            :link="projects.cta"
            class="cta"
          />
        </div>
      </div>
    </section>

    <section
      class="counter"
      v-if="counter.end_date"
      scroll-animation
    >
      <div class="wrapper counter__wrapper">
        <h2
          class="counter__title margin__b--60"
          v-if="counter.title && $i18n.translate(counter.title)"
        >
          {{ $i18n.translate(counter.title) }}
        </h2>

        <ul class="counter__list">
          <li
            class="counter__item"
            v-for="item in counter_date"
            :key="`counter_${item.label}`"
          >
            <strong class="counter__value">
              {{ item.value }}
            </strong>

            <span class="counter__label">
              {{ $i18n.t(`literal.${item.label}`) }}
            </span>
          </li>
        </ul>
      </div>
    </section>

    <section
      class="partners"
      v-if="partners.logos.length > 0"
      scroll-animation
    >
      <div class="wrapper">
        <h2
          class="partners__title margin__b--48"
          v-if="partners.title && $i18n.translate(partners.title)"
        >
          {{ $i18n.translate(partners.title) }}
        </h2>

        <ul class="partners__logos">
          <li
            v-for="(partner, i) in partners.logos"
            :key="`partner_${i}`"
            class="partners__item partner"
          >
            <app-link
              :link="partner.link"
              class="partner__link"
              v-if="partner.link && partner.link.show"
              v-slot="{ text }"
            >
              <img
                :src="$i18n.translate(partner.logo)"
                :alt="text"
                class="partner__logo"
              >
            </app-link>

            <img
              :src="$i18n.translate(partner.logo)"
              :alt="partner.link.text"
              class="partner__logo"
              v-else
            >
          </li>
        </ul>
      </div>
    </section>
  </main>
</template>

<script>
import { defineAsyncComponent, defineComponent } from 'vue'
import { useProjectStore } from '@/stores/ProjectStore'
import { useScrollAnimation } from '@/composables/modules/pages'
import { intervalToDuration, isFuture } from 'date-fns'
import { get as _get } from 'lodash'

import ProjectCard from '&/organisms/ProjectCard'

export default defineComponent({
  name: 'CustomHomepage24hTelevie',
  components: {
    ProjectCard,
    AppLink: defineAsyncComponent(() => import('&/atoms/AppLink')),
    AppContent: defineAsyncComponent(() => import('&/atoms/AppContent')),
    AppVideoEmbed: defineAsyncComponent(() => import('&/atoms/AppVideoEmbed')),
    AppCounter: defineAsyncComponent(() => import('&/molecules/AppCounter'))
  },
  props: {
    section: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      index: 0,
      width: 0,
      has_transition: true,
      counter_date: {
        hours: { label: 'hours', value: '00' },
        minutes: { label: 'minutes', value: '00' },
        seconds: { label: 'seconds', value: '00' },
      },
      interval: null
    }
  },
  setup (props) {
    const { getProjects } = useProjectStore()

    const projects = _get(props.section, 'attributes.projects', {})

    const { projects: project_cards } = getProjects({ query: _get(projects, 'query', { 'sort_by': 'amount_first', 'per_page': 4 }) })

    return {
      projects,
      project_cards,
      ...useScrollAnimation()
    }
  },
  created () {
    if (this.counter.end_date && isFuture(new Date(this.counter.end_date)))
      this.interval = setInterval(() => this.setCounter(), 1000)
  },
  mounted () {
    window.addEventListener('resize', this.onResize)

    this.onResize()
  },
  computed: {
    hero () {
      return _get(this.section, 'attributes.hero', {})
    },
    organisers () {
      return _get(this.hero, 'organisers', {})
    },
    stats () {
      return _get(this.section, 'attributes.stats', {})
    },
    metrics () {
      return _get(this.stats, 'metrics', []).slice(0, 3)
    },
    sports () {
      return _get(this.section, 'attributes.sports', [])
    },
    icons () {
      return {
        running: '<svg width="129" height="75" enable-background="new 0 0 129 75" version="1.1" viewBox="0 0 129 75" xml:space="preserve" xmlns="http://www.w3.org/2000/svg"><path d="m128.9 53.6c-0.3-2.2-1.7-4-3.8-4.8-2.9-1.1-5.9-1.9-8.9-2.6-4.6-1.1-9-2.4-13.4-4.1l-2.3-3.7 1.3-0.7c0.9-0.5 1.3-1.6 0.8-2.6-0.5-0.9-1.6-1.3-2.6-0.8l-1.4 0.7-1.7-2.9 1.1-0.6c0.9-0.5 1.3-1.6 0.8-2.6-0.5-0.9-1.6-1.3-2.6-0.8l-1.3 0.7-1-1.7 1.1-0.6c0.6-0.3 1-0.9 1-1.6s-0.3-1.3-0.9-1.7-1.3-0.4-1.9-0.1l-1.3 0.7-1.5-2.5 1.6-0.8c0.9-0.5 1.3-1.6 0.8-2.6-0.5-0.9-1.6-1.3-2.6-0.8l-1.9 1-1.5-2.5c-0.5-0.7-1.4-1.1-2.2-0.8-4.3 1.5-10.6 2.8-15.6-0.1-3.9-2.3-6.5-6.7-7.6-13.1-0.1-0.8-0.8-1.4-1.6-1.5-11-1.5-19.9 5.3-26.4 20.4-1.9 0.9-5.8 3.3-7.6 7.3-1.2 2.8-1.1 6 0.1 8.8 4.3 10.5 26.4 14.7 27.3 14.8h0.3c0.1 0 5.6 0.2 13.4 9.6 7.8 9 19 14.1 30.7 14 2.6 0 5.2-0.2 7.8-0.8 13.7-2.8 21.9-14.4 22.3-14.9 0.2-0.2 0.3-0.5 0.3-0.8 0.8-1.5 1.1-3.2 0.9-4.9zm-70.9-49.9c1.5 6.8 4.5 11.6 9.2 14.3 4.6 2.7 10.6 3 17.3 0.9l0.6 1-1 0.5c-0.8 0.4-1.2 1.3-1 2.2s1 1.5 1.9 1.5c0.3 0 0.6-0.1 0.9-0.2l1.2-0.6 1.5 2.5-1.6 0.6c-0.8 0.4-1.2 1.3-1 2.2s1 1.5 1.9 1.5c0.3 0 0.6-0.1 0.9-0.2l1.7-0.9 1 1.7-1.5 0.7c-0.8 0.4-1.2 1.3-1 2.2s1 1.5 1.9 1.5c0.3 0 0.6-0.1 0.9-0.2l1.8-0.9 1.7 2.9-1.3 0.5c-0.8 0.4-1.2 1.3-1 2.2s1 1.5 1.9 1.5c0.3 0 0.6-0.1 0.9-0.2l1.5-0.8 1.8 3c-2.3 1.1-4.3 2.9-5.6 5.2-0.9 1.6-2.2 3.1-4.1 4.2-2.8-1.7-5-4.3-6.2-7.4-1.5-3.9-2.5-7.9-2.9-12.1-0.2-1.4-0.6-2.7-1.4-3.8-0.7-1.2-1.7-2.2-2.8-3-2.2-1.6-5-2.1-7.7-1.6-1.7 0.3-3.3 0.4-5 0.5-3 0.1-6-0.8-8.5-2.6-0.1-6.5-4.3-12.1-7.6-15.5 3.3-2.5 6.9-3.6 10.7-3.3zm18.8 47.4h-0.1c-2.3-2.5-8.4-8.1-19.8-13.8-2.5-1.2-4.6-2.4-6.5-3.6 2.1-2.3 3.5-4.7 4.2-7.3 2.8 1.5 5.9 2.3 9.1 2.2 1.9 0 3.8-0.2 5.7-0.5 2.6-0.5 5.3 0.6 6.7 2.9 0.5 0.7 0.7 1.6 0.9 2.4 0.5 4.5 1.6 8.9 3.2 13.1 1.4 2.9 3 5.2 4.8 6.9-2.4 0-5.1-0.7-8.2-2.3zm-32.1-41.8c2.6 2.7 6.8 7.8 6.7 13.4-0.2 3.5-1.7 6.7-4.2 9-7.4-5.1-9.7-9-10.2-10.1 2.3-5.3 4.9-9.4 7.7-12.3zm59.9 61.3c-12.9 2.6-26.1-1.9-34.8-11.7-8.3-10.1-14.6-10.9-16-11-5.9-1.1-21.5-5.5-24.4-12.6-0.9-1.9-1-4-0.2-5.9 1.1-2.2 2.9-4 5-5.1 1.3 2.2 4.5 6.2 11.5 10.9 0.3 0.4 0.7 0.6 1.1 0.7 2.3 1.5 5.1 3 8.3 4.6 14 6.9 19.6 13.9 19.7 14 0 0.1 0.1 0.1 0.1 0.2 9 9.1 14.8 10.5 15 10.5 9.8 2.5 22.2-0.4 29.9-2.8-4.2 4.1-9.5 6.9-15.2 8.2zm-13.8-8.9s-3-0.8-8.1-4.9c0.7 0.1 1.5 0.2 2.3 0.2 5-0.1 9.6-2.8 11.8-7.1 1.1-2 3-3.5 5.3-4.2 4.3 1.6 8.8 3 13.3 4.1 2.9 0.6 5.7 1.5 8.5 2.5 0.8 0.3 1.3 0.9 1.4 1.7 0.1 1-0.1 2.1-0.6 3-4 1.4-21.6 7.7-33.9 4.7z" fill="#f40b0b"/><path d="m3.8 53h31.3c1 0 1.8-0.9 1.8-2s-0.8-2-1.8-2h-31.3c-1 0-1.8 0.9-1.8 2s0.8 2 1.8 2z" fill="#f40b0b"/><path d="m35 62c0-1.1-0.8-2-1.8-2h-31.4c-1 0-1.8 0.9-1.8 2s0.8 2 1.8 2h31.3c1.1 0 1.9-0.9 1.9-2z" fill="#f40b0b"/><path d="m39.2 71h-31.4c-1 0-1.8 0.9-1.8 2s0.8 2 1.8 2h31.3c1 0 1.8-0.9 1.8-2s-0.7-2-1.7-2z" fill="#f40b0b"/></svg>',
        handbike: '<svg enable-background="new 0 0 167 83" width="167" height="83" version="1.1" viewBox="0 0 167 83" xml:space="preserve" xmlns="http://www.w3.org/2000/svg"><path d="m163.3 45c-3.1-4.9-7.3-8.4-12.8-10.3-6.6-2.3-13-1.9-19.4 0.9-0.2-0.3-0.4-0.5-0.7-0.7-6.1-7.4-12.1-14.8-18.2-22.2-2-2.5-4-4.9-6-7.4-3.6-4.3-8.3-6.1-13.8-5-5.9 1.1-10.9 6.3-11.1 13.3-0.1 3.1 0.8 5.9 2.6 8.4 0.2 0.2 0.3 0.5 0.5 0.7-0.6 0.8-2.9 3.2-3.5 3.6-0.1 0-0.1 0-0.2-0.1-0.4-0.4-0.8-0.8-1.2-1.1-0.3-0.2-0.5-0.5-0.8-0.7-0.7-0.6-1.7-0.5-2.3 0.1s-0.6 1.5-0.1 2.2c0.1 0.2 0.3 0.3 0.5 0.5l6 5.7 0.4 0.4c0.7 0.5 1.7 0.5 2.3-0.2 0.6-0.6 0.6-1.6 0-2.2l-2.1-2.1c0.7-0.9 3-3.3 3.6-3.7 0.4 0.2 0.7 0.5 1.1 0.8 1.8 1.3 3.7 2.7 5.5 4 3.9 2.8 7.8 5.7 11.6 8.5 0.2 0.2 0.5 0.3 0.6 0.6-0.2 0.2-0.4 0.5-0.6 0.7-2.8 2.9-5.6 5.9-8.4 8.8-2.3 2.4-4.7 4.9-7 7.3-0.4 0.4-0.7 0.6-1.3 0.6h-2.6c-0.2 0-0.4 0-0.7-0.1-0.1-0.3-0.1-0.6-0.1-0.9-0.5-2.7-2.9-4.2-5-4.1h-0.9-21.3-1.1c-0.2-0.4-0.3-0.7-0.5-1-4.2-9.6-8.3-19.1-12.5-28.7-0.2-0.4-0.3-0.7-0.5-1.1-0.7-1.1-2-1.5-3.1-1-1.2 0.5-1.8 1.7-1.5 2.9l0.3 0.9c2.2 5 4.3 9.9 6.5 14.9 2.5 5.7 4.9 11.3 7.4 17 0.1 0.3 0.4 0.6 0.3 1-0.8 0.2-2.6 0.2-3.4 0 0-0.2-0.1-0.3-0.1-0.5-0.1-1.2-0.3-2.4-0.6-3.5-3.1-12.7-15.8-21.4-29.6-18.5-13.5 3-22 16.5-18.9 30 3.1 13.3 16.7 22.1 30.8 18.5 9.7-2.5 17.5-11 18.5-21.7 0-0.2 0.1-0.4 0.2-0.7h1.3 52.8 0.9c0.5 0 0.9-0.2 1.3-0.6l0.5-0.5c3.4-3.5 6.7-7 10.1-10.6 0.2-0.2 0.4-0.6 0.8-0.7 0.4 0.3 0.9 0.5 1.2 0.9-4.9 12.2 0.1 24.7 9.8 30.8 9.6 6 22.4 4.9 30.9-3.5 8.2-8 9.7-20.8 3.6-30.6zm-136.1 7.8c-1.2-0.5-2.5-0.6-3.7-0.2-2.5 0.7-4.2 3-4.2 5.5 0 2.6 1.8 4.9 4.2 5.5 1.5 0.4 3 0.2 4.3-0.5s2.2-1.9 2.7-3.3c1.2-0.1 13.5-0.1 14.2 0 0.3 2.3-1.6 7.4-4 10.4-3.4 4.3-7.9 6.9-13.4 7.5-11.9 1.4-21.5-7.5-22.2-18.2-0.8-11.2 8.1-21.1 19.7-21.2 12-0.1 19.6 9.6 20 18-0.3 0.2-0.6 0.1-0.9 0.1h-10-2.5-0.7c-0.8-1.7-1.8-2.9-3.5-3.6zm0.2 5.3c0 1.3-1.2 2.4-2.5 2.4s-2.4-1.2-2.3-2.4c0.1-1.4 1.1-2.3 2.4-2.3 1.3-0.1 2.4 1 2.4 2.3zm103.9-16.7c0.1 0.1 0.3 0.2 0.4 0.3 2.9 3.5 5.7 7 8.6 10.5 0.1 0.1 0.1 0.2 0.2 0.3-0.3 0.3-0.8 0.3-1.2 0.6-0.9-0.8-1.8-1.7-2.6-2.5-0.9-0.8-1.7-1.6-2.5-2.4-0.9-0.8-1.7-1.6-2.6-2.5-0.8-0.8-1.7-1.6-2.5-2.5 0.5-0.7 1.3-1.3 2.2-1.8zm10.7 14.3c1.3 0 2.3 1.1 2.4 2.4 0 1.3-1 2.4-2.3 2.4s-2.4-1-2.4-2.3c-0.1-1.1 0.7-2.4 2.3-2.5zm-5.3-0.2c0 0.4-0.2 0.8-0.4 1.2h-0.1-0.1c-3.7-2.7-7.3-5.4-11-8l-0.4-0.4c0.5-0.9 1.1-1.8 1.8-2.5 0.6 0.5 9.3 8.7 10.2 9.7zm-27.4-41.1c6.3 7.7 12.5 15.3 18.7 22.9-0.4 0.5-1 0.8-1.5 1.2s-1 0.8-1.4 1.2c-0.6-0.4-17.9-16.8-18.5-17.5 2.4-4.4 2.1-4.3 2.7-7.8zm-18.9 8.8c-0.3-0.2-0.7-0.3-1-0.7 1.4-1.5 2.8-2.9 4.2-4.4 0.3 0.1 0.6 0.1 0.9 0.1 2.4 0.5 4.7-1.1 5.2-3.4 0.5-2-0.6-4.7-3.5-5.3-2.3-0.5-4.7 1-5.3 3.4-0.2 0.7-0.1 1.4 0 2.1 0.1 0.2 0.1 0.4 0.2 0.6-0.4 0.6-3.3 3.7-4.2 4.5-0.2 0-0.3-0.2-0.4-0.4-4.1-6.2-0.8-14.6 6.6-16.1 6-1.2 11.2 2.7 12.4 7.9 1.4 5.9-2.6 11.7-8.5 12.7-2.3 0.4-4.5 0.1-6.6-1zm3.8-9.3c0-0.6 0.5-1.1 1.1-1.1s1 0.5 1 1.1-0.4 1.1-1 1.1-1.1-0.5-1.1-1.1zm2.8 14.3-0.4-0.4c1-0.2 1.8-0.4 2.7-0.6s1.7-0.6 2.5-1 1.5-0.9 2.4-1.5c2.7 2.6 5.4 5.1 8 7.6-0.4 0.7-2.7 3.1-3.9 4.1-0.1-0.1-0.3-0.2-0.5-0.3-3.5-2.6-7.1-5.3-10.8-7.9zm17.4 17.7c-3.2 3.3-6.3 6.6-9.5 9.9l-0.2 0.2c-0.3 0.3-0.6 0.5-1 0.4h-0.4-8.1-1c0-0.4 0.3-0.5 0.4-0.7 1.8-1.9 3.7-3.8 5.5-5.8 2.6-2.7 5.2-5.4 7.7-8.1 0.3-0.3 0.5-0.6 0.8-0.8 0.8 0.4 1.4 0.9 2 1.4 0.7 0.5 1.4 1 2 1.5s1.3 0.9 2 1.5c0 0.2-0.1 0.3-0.2 0.5zm2.8-3.1c-0.1-0.1-0.3-0.1-0.4-0.2-1.8-1.3-3.6-2.6-5.3-3.9-0.1-0.1-0.2-0.2-0.4-0.3 1.2-1.3 2.4-2.5 3.6-3.8 1.8 1.7 3.6 3.4 5.3 5-0.3 0.8-1.6 2.2-2.8 3.2zm4.6 0.6c-0.4 0.6-0.8 1.1-1.2 1.7-0.1 0.2-0.2 0.1-0.5-0.2 0.5-0.7 1.2-1.3 1.9-1.8-0.1 0.1-0.1 0.2-0.2 0.3zm35.8 27c-6.3 7.9-17.2 9.8-25.6 4.9-9.1-5.3-11.7-15.7-8.8-23.7 0.3 0.2 0.6 0.3 0.8 0.5 4.7 3.5 9.5 6.9 14.2 10.4 0.8 0.6 1.6 1 2.5 1.2 3.3 0.7 6.6-1.6 6.9-5 0.2-1.7-0.3-3.1-1.4-4.4-1.5-1.8-3-3.6-4.4-5.4-2.3-2.8-4.6-5.6-6.8-8.4-0.2-0.2-0.3-0.5-0.5-0.7 1.5-1 5.9-1.9 9.8-1.4 4.8 0.6 8.8 2.6 12.1 6.1 6.9 7.1 7.4 18.1 1.2 25.9z" clip-rule="evenodd" fill="#f40b0b" fill-rule="evenodd"/></svg>',
        boots: '<svg enable-background="new 0 0 109 75" width="109" height="75" version="1.1" viewBox="0 0 109 75" xml:space="preserve" xmlns="http://www.w3.org/2000/svg"><path class="st0" d="m107.9 59.2c-0.1-0.6-0.6-1.1-1.3-1.1h-0.5c-0.5-2.7-2.6-8.9-13.2-9.4-2.8-0.2-5.6-0.6-8.4-1.4h-0.2c-9.8-2.5-18.6-7.6-25.7-14.9 0-0.1-0.1-0.1-0.1-0.2-0.1-0.1-0.2-0.3-0.4-0.3-1.6-1.8-3.1-3.7-4.5-5.7v-0.2c-0.1-0.1-0.1-0.2-0.2-0.2-1.1-1.6-2-3.3-2.9-5.1 0-0.2-0.1-0.3-0.2-0.5-2.5-5.3-4-10.9-4.6-16.7-0.2-1.6-1.4-2.8-3-3.1-4.6-1.4-13.7 1-16.6 4.4-0.6 0.7-1 1.5-1 2.4l-20.1 2.6c-1.1 0.1-2.1 0.7-2.8 1.6s-1 2-0.8 3.1l0.8 6.1c0.2 1.5 1.2 2.7 2.5 3.3 0.3 3.9 0.3 7.9-0.1 11.8-0.2 1.5-0.5 3-1 4.9-1.6 6.3-2.1 12.9-1.4 19.4l-1.2 0.9c-0.3 0.2-0.5 0.6-0.5 1l-0.5 10.5c0 0.6 0.4 1.2 1.1 1.3l8.1 1.3h0.2c0.4 0 0.8-0.2 1.1-0.6 0.1-0.2 0.4-0.6 0.7-1 0.3 0.3 0.5 0.6 0.7 1s0.6 0.6 1.1 0.6h6.8c0.5 0 0.9-0.2 1.1-0.6 0.2-0.3 0.3-0.5 0.5-0.7 0.2 0.2 0.4 0.5 0.5 0.7 0.2 0.4 0.6 0.6 1.1 0.6h8.9c0.3 0 0.6-0.1 0.8-0.3l4.8-4.3c2.1 0.1 7.5 1 10.4 1.5v1.2c0 0.7 0.5 1.2 1.2 1.3l8.3 0.6c0.6 0 1.1-0.3 1.3-0.8 0.1-0.2 0.2-0.4 0.3-0.5l0.3 0.6c0.2 0.5 0.7 0.8 1.2 0.8h7.1c0.6 0 1.1-0.4 1.3-1 0-0.2 0.1-0.3 0.2-0.5 0.2 0.2 0.3 0.5 0.5 0.7 0.2 0.4 0.6 0.7 1.1 0.7h7.1c0.6 0 1.1-0.4 1.3-1 0-0.2 0.1-0.4 0.2-0.6 0 0.1 0.1 0.1 0.1 0.2 0.2 0.5 0.8 0.8 1.4 0.7l7-1.1c0.5-0.1 1-0.5 1.1-1.1 0-0.2 0.1-0.3 0.1-0.5l0.2 0.3c0.3 0.4 0.8 0.6 1.3 0.5l6.8-1.6c0.5-0.1 0.9-0.6 1-1.1v-0.2c0.3 0.5 0.8 0.7 1.4 0.6 2.9-0.8 5.8-1.9 8.5-3.2 0.5-0.2 0.7-0.7 0.7-1.3l-1-6.4zm-23.6-9.3c2.8 0.7 5.6 1.2 8.5 1.4 9.3 0.5 10.7 5.6 10.9 7.5-1.6 0.9-7.5 3.7-22.2 5.4-1.7-9.8 1.6-13.4 2.8-14.3zm-56.3-43.5c2.2-2.6 10.3-4.7 13.9-3.6 1.1 0.3 1.2 0.8 1.3 1 0.3 3.1 0.9 6.1 1.7 9.1h-4.1c-0.6-1.5-2.2-2.4-3.8-2.1s-2.8 1.7-2.8 3.4 1.2 3 2.8 3.4c1.6 0.3 3.2-0.6 3.8-2.1h4.9c0.5 1.6 1.1 3.2 1.7 4.7l-4.4 0.8c-0.7-1.5-2.4-2.3-4-1.8s-2.7 1.9-2.5 3.6c0.1 1.6 1.4 3 3 3.2s3.2-0.8 3.7-2.4l5.3-1.2c0.7 1.4 1.4 2.6 2.2 3.9l-3.5 2.1c-0.6-0.6-1.5-0.9-2.3-0.9-1.9 0-3.4 1.5-3.4 3.4s1.5 3.4 3.4 3.4 3.4-1.5 3.4-3.4v-0.2l3.8-2.2c1.1 1.6 2.3 3.1 3.5 4.5l-2.2 1.7c-0.4-0.2-0.8-0.3-1.3-0.3-1.4 0-2.7 0.8-3.2 2.1-0.6 1.3-0.3 2.8 0.7 3.8s2.5 1.3 3.7 0.8c1.3-0.5 2.1-1.8 2.1-3.2 0-0.5-0.1-0.9-0.3-1.4l2.1-1.6c6.7 6.7 15.1 11.7 24.2 14.3-1.1 1.3-1.9 2.9-2.3 4.6-40.6 2.8-58.9-11.4-61.6-13.8-0.1-5.7-0.6-11.4-1.7-16.9l13.8-1.8c1.1-0.2 2.1-0.7 2.8-1.6s1-2 0.8-3.1l-0.8-6.1c-0.3-2.5-2.4-4.1-4.6-3.8 0-0.1 0.1-0.2 0.2-0.3zm10.4 7.7c0 0.4-0.2 0.8-0.6 0.9-0.3 0.1-0.7 0.1-1-0.2s-0.3-0.6-0.2-1 0.5-0.6 0.8-0.5c0.6 0 1 0.3 1 0.8zm2.5 8.4c0 0.6-0.4 1-0.9 1s-0.9-0.4-0.9-0.9 0.4-0.9 0.9-0.9c0.4 0 0.8 0.3 0.9 0.8zm4.9 8.4c0 0.4-0.2 0.8-0.5 0.9s-0.7 0.1-1-0.2-0.3-0.6-0.2-1c0.1-0.3 0.5-0.6 0.8-0.5 0.4-0.1 0.8 0.3 0.9 0.8zm7.1 6.9c0 0.5-0.4 0.9-0.9 0.9s-0.9-0.4-0.9-0.9 0.4-0.9 0.9-0.9c0.2 0 0.5 0.1 0.6 0.3 0.3 0.1 0.4 0.3 0.3 0.6zm26 26.6c-1.3 0.1-2.5 0.3-3.9 0.4-12.6 1.1-25.3 0.2-37.7-2.8-2.9-0.6-5.3-1.1-7.6-1.4-1.2-0.2-2.4-0.3-3.7-0.4l-0.3-0.3c-0.1 0-8.9-6.2-8.2-16.8 5.3 4 20.9 13.5 51.1 13.5 3.2 0 6.6-0.1 10.1-0.3-0.3 2.6-0.2 5.4 0.2 8.1zm-74.2-44.2-0.8-6.1c-0.1-0.4 0-0.9 0.3-1.2s0.6-0.5 1.1-0.6l22.8-3h0.2c0.4 0 0.7 0.1 1 0.3 0.3 0.3 0.6 0.6 0.6 1.1l0.8 6.1c0.1 0.4-0.1 0.8-0.3 1.2-0.3 0.3-0.6 0.6-1.1 0.6l-22.8 3c-0.4 0.1-0.9-0.1-1.2-0.3-0.3-0.3-0.6-0.7-0.6-1.1zm1.4 20.8c0.4-1.8 0.8-3.5 1-5.1 0.4-3.9 0.5-7.9 0.1-11.8l6.1-0.8c1.2 6.5 1.7 13.1 1.6 19.7-0.3 6.4 2.2 12.6 6.9 16.9-6.9-0.4-14.4-0.5-17.2-0.6-0.6-6.1-0.1-12.3 1.5-18.3zm93.6 26.3c-0.3-0.7-0.9-1.2-1.7-1.4-0.5-0.1-1.1 0.1-1.4 0.5-0.5 0.6-0.9 1.3-1 2.1l-5.1 1.3c-1-1.1-2-1.5-2.9-0.9-0.6 0.5-1.1 1.2-1.3 2l-5.2 0.8c-0.7-1.3-1.6-1.9-2.5-1.7-0.4 0.1-1.3 0.2-1.9 2.6h-5.4c-0.5-0.9-1.5-1.9-2.7-1.7-0.9 0.2-1.7 0.9-2 1.7h-5.5c-0.4-0.7-0.9-1.2-1.6-1.5-0.5-0.2-1-0.1-1.5 0.2s-1 0.7-1.3 1.3l-6.4-0.6v-1.1c0-0.6-0.4-1.2-1-1.3-0.4-0.1-9.9-1.8-12.4-1.8-0.3 0-0.6 0.1-0.8 0.3l-4.8 4.3h-7.7c-0.6-0.8-1.6-1.8-2.9-1.4-0.7 0.3-1.3 0.8-1.7 1.4h-5.4c-1.4-2-2.2-2.1-2.5-2.1-0.5 0-1 0.2-1.3 0.6s-0.7 1-1 1.4l-6.2-1.1 0.4-8.7 0.9-0.7c2.6 0 17.4 0.2 25.5 1.3 2.2 0.3 4.6 0.8 7.4 1.4 9 2.1 18.2 3.2 27.4 3.2 3.4 0 7.1-0.1 11-0.5 21.1-1.8 28.4-5.6 30.1-6.7h0.1l0.4 4.3c-1.9 1-4 1.8-6.1 2.5z" fill="#f40b0b"/></svg>',
        bike: '<svg enable-background="new 0 0 134 83" width="134" height="83" version="1.1" viewBox="0 0 134 83" xml:space="preserve" xmlns="http://www.w3.org/2000/svg"><path d="m105.5 27.4c-3.8 0-7.5 0.8-11 2.2l-4.7-9.1v-14.3c0-0.8-0.5-1.6-1.4-1.9l-11-4.1c-1.1-0.4-2.2 0.1-2.6 1.2-0.3 1.1 0.2 2.2 1.3 2.6l9.7 3.6v11.7h-40.1v-4h4.9c1.1 0 2.1-1 2.1-2.1s-1-2.1-2.1-2.1h-12.7c-1.1 0-2.1 1-2.1 2.1s1 2.1 2.1 2.1h3.7v5.6l-4 8.6c-3-1.2-6.4-1.9-9.9-1.9-15.2 0-27.7 12.3-27.7 27.7s12.3 27.7 27.7 27.7c14.5 0 26.5-11.4 27.6-25.7h8.9c0.7 0 1.2-0.3 1.6-0.8l21.5-31.1 3.4 6.4c-7.8 4.7-13 13.4-13 23.3 0 15.2 12.3 27.7 27.7 27.7s27.7-12.3 27.7-27.7c0.1-15.3-12.2-27.7-27.6-27.7zm-77.8 51.3c-13 0-23.6-10.6-23.6-23.6s10.6-23.6 23.6-23.6c2.9 0 5.6 0.5 8.1 1.5l-10 21.3v0.1c-0.1 0.3-0.1 0.4-0.1 0.7v0.1c0 0.3 0 0.4 0.1 0.7v0.1c0 0.1 0 0.1 0.1 0.3 0 0.1 0.1 0.1 0.3 0.3v0.1c0.1 0.1 0.4 0.3 0.5 0.4h0.1c0.3 0.1 0.4 0.1 0.7 0.1h23.5c-0.9 12.1-10.9 21.5-23.3 21.5zm3.2-25.6 8.6-18.2c6.4 3.7 11 10.4 11.7 18.2h-20.3zm32.2 0h-8c-0.7-9.5-6.2-17.6-14-22.1l3.7-7.8h38.8l-20.5 29.9zm42.3 25.6c-13 0-23.6-10.6-23.6-23.6 0-8.4 4.4-15.6 11-19.9l10.8 20.9c0.4 0.7 1.1 1.1 1.8 1.1 0.3 0 0.7-0.1 1-0.3 1-0.5 1.4-1.8 0.8-2.7l-10.9-20.9c2.9-1.1 5.9-1.8 9.1-1.8 13 0 23.6 10.6 23.6 23.6 0 13.1-10.6 23.6-23.6 23.6z" fill="#f40b0b"/></svg>',
        swim: '<svg enable-background="new 0 0 83 75" width="83" height="75" version="1.1" viewBox="0 0 83 75" xml:space="preserve" xmlns="http://www.w3.org/2000/svg"><path d="m78.2 22.2c-1.9-3.7-4.4-7.1-7.4-10-4.6-4.7-10.4-8.2-16.7-10.2-0.1 0-0.1 0-0.2-0.1-8-2.5-16.9-2.5-24.9 0-0.1 0-0.1 0-0.2 0.1-10.2 3.2-19 10.6-24 20.2-3.2 5.9-4.8 12.6-4.8 19.3v8.3c0 0.4 0.2 0.9 0.5 1.1 0.3 0.3 0.8 0.4 1.2 0.3 3.8-0.9 7.7-1.7 11.6-2.4 6.4-1.1 12.8-1.8 19.1-2.2 6-0.3 12.1-0.3 18.2 0 6.3 0.3 12.7 1.1 19.1 2.2 3.9 0.6 7.8 1.4 11.6 2.4h0.3c0.3 0 0.6-0.1 0.9-0.3 0.3-0.3 0.5-0.7 0.5-1.1v-8.3c0-6.7-1.6-13.4-4.8-19.3zm-47.3-17.8c3.4-1 7-1.5 10.6-1.5s7.2 0.5 10.6 1.5c0.3 10.9-0.3 21.9-1.7 32.8-5.9-0.3-11.9-0.3-17.8 0-1.4-11-2-22-1.7-32.8zm49.2 43.6c-3.3-0.8-6.7-1.4-10-2-6.5-1.1-13-1.9-19.4-2.2-6.1-0.4-12.4-0.4-18.5 0-6.4 0.4-12.9 1.1-19.4 2.2-3.3 0.6-6.7 1.2-10 2v-6.5c0-6.2 1.5-12.5 4.4-18 4.5-8.4 12-14.9 20.8-18.2-0.2 11.1 0.4 22.4 1.9 33.5 0.1 0.7 0.8 1.3 1.5 1.2 6.7-0.4 13.4-0.4 20.1 0h0.1c0.7 0 1.3-0.5 1.4-1.2 1.5-11.1 2.1-22.4 1.9-33.5 5.2 1.9 9.9 5 13.8 8.9 2.8 2.8 5.1 5.9 6.9 9.3 2.9 5.5 4.4 11.7 4.4 18v6.5z" fill="#f40b0b"/><path d="m20.3 63.1-3.1 3.1c-0.6 0.6-0.6 1.5 0 2 0.3 0.3 0.6 0.4 1 0.4s0.7-0.1 1-0.4l3.1-3.1c0.6-0.6 0.6-1.5 0-2-0.6-0.6-1.5-0.6-2 0z" fill="#f40b0b"/><path d="m26.5 63.1-3.1 3.1c-0.6 0.6-0.6 1.5 0 2 0.3 0.3 0.6 0.4 1 0.4s0.7-0.1 1-0.4l3.1-3.1c0.6-0.6 0.6-1.5 0-2-0.5-0.6-1.5-0.6-2 0z" fill="#f40b0b"/><path d="m57.6 63.1-3.1 3.1c-0.6 0.6-0.6 1.5 0 2 0.3 0.3 0.6 0.4 1 0.4s0.7-0.1 1-0.4l3.1-3.1c0.6-0.6 0.6-1.5 0-2-0.5-0.6-1.4-0.6-2 0z" fill="#f40b0b"/><path d="m63.8 63.1-3.1 3.1c-0.6 0.6-0.6 1.5 0 2 0.3 0.3 0.6 0.4 1 0.4s0.7-0.1 1-0.4l3.1-3.1c0.6-0.6 0.6-1.5 0-2-0.5-0.6-1.4-0.6-2 0z" fill="#f40b0b"/><path d="m75.7 61.1h-2.4c-1.2-2.8-4-4.7-7.2-4.7h-12c-3 0-5.6 1.7-6.9 4.2-3.8-1.5-7.7-1.5-11.5 0-1.3-2.5-4-4.2-6.9-4.2h-12c-3.1 0-5.9 1.9-7.2 4.7h-2.3c-1.6 0-3 1.3-3 3v3.1c0 1.6 1.3 3 3 3h2.4c1.2 2.8 4 4.7 7.2 4.7h12c3.3 0 6.2-2 7.3-5 3.6-1.6 7.1-1.6 10.7 0 1.2 3 4.1 5 7.3 5h12c3.1 0 5.9-1.9 7.2-4.7h2.4c1.6 0 3-1.3 3-3v-3.1c-0.1-1.7-1.4-3-3.1-3zm-66.7 6.2h-1.7c-0.1 0-0.1-0.1-0.1-0.1v-3.1c0-0.1 0-0.1 0.1-0.1h1.7v0.4 2.6 0.3zm24.8-0.3c0 0.5-0.1 0.9-0.2 1.4-0.6 2.1-2.5 3.6-4.8 3.6h-12c-2.2 0-4.2-1.5-4.8-3.7-0.1-0.4-0.2-0.9-0.2-1.4v-2.6c0-0.5 0.1-0.9 0.2-1.4 0.6-2.1 2.6-3.6 4.8-3.6h12c2.2 0 4.2 1.5 4.8 3.7 0.1 0.4 0.2 0.9 0.2 1.4v2.6zm12.5-2.7v2.2c-3.2-1-6.4-1-9.6 0v-2.2c0-0.3 0-0.6-0.1-0.9 3.3-1.4 6.5-1.4 9.7 0v0.9zm24.9 2.7c0 0.5-0.1 0.9-0.2 1.4-0.6 2.1-2.6 3.6-4.8 3.6h-12c-2.2 0-4.2-1.5-4.8-3.7-0.1-0.4-0.2-0.9-0.2-1.4v-2.6c0-0.5 0.1-0.9 0.2-1.4 0.6-2.1 2.6-3.6 4.8-3.6h12c2.2 0 4.2 1.5 4.8 3.7 0.1 0.4 0.2 0.9 0.2 1.4v2.6zm4.6 0.2c0 0.1-0.1 0.1-0.1 0.1h-1.7v-0.4-2.6-0.4h1.7c0.1 0 0.1 0.1 0.1 0.1v3.2z" fill="#f40b0b"/></svg>',
        users: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" width="120"><path fill="#f40b0b" d="M319.9 320c57.41 0 103.1-46.56 103.1-104c0-57.44-46.54-104-103.1-104c-57.41 0-103.1 46.56-103.1 104C215.9 273.4 262.5 320 319.9 320zM319.9 144c39.68 0 71.96 32.3 71.96 72S359.5 288 319.9 288S247.9 255.7 247.9 216S280.2 144 319.9 144zM369.9 352H270.1C191.6 352 128 411.7 128 485.3C128 500.1 140.7 512 156.4 512h327.2C499.3 512 512 500.1 512 485.3C512 411.7 448.4 352 369.9 352zM160.2 480c3.021-53.41 51.19-96 109.1-96H369.9c58.78 0 106.9 42.59 109.1 96H160.2zM512 160c44.18 0 80-35.82 80-80S556.2 0 512 0c-44.18 0-80 35.82-80 80S467.8 160 512 160zM512 32c26.47 0 48 21.53 48 48S538.5 128 512 128s-48-21.53-48-48S485.5 32 512 32zM128 160c44.18 0 80-35.82 80-80S172.2 0 128 0C83.82 0 48 35.82 48 80S83.82 160 128 160zM128 32c26.47 0 48 21.53 48 48S154.5 128 128 128S80 106.5 80 80S101.5 32 128 32zM561.1 192H496c-11.16 0-22.08 2.5-32.47 7.438c-7.984 3.797-11.39 13.34-7.594 21.31s13.38 11.39 21.31 7.594C483.3 225.5 489.6 224 496 224h65.08C586.1 224 608 246.7 608 274.7V288c0 8.844 7.156 16 16 16S640 296.8 640 288V274.7C640 229.1 604.6 192 561.1 192zM162.8 228.3c7.938 3.797 17.53 .375 21.31-7.594c3.797-7.969 .3906-17.52-7.594-21.31C166.1 194.5 155.2 192 144 192H78.92C35.41 192 0 229.1 0 274.7V288c0 8.844 7.156 16 16 16S32 296.8 32 288V274.7C32 246.7 53.05 224 78.92 224H144C150.4 224 156.7 225.5 162.8 228.3z"/></svg>',
        heart: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="120"><path fill="#f40b0b" d="M255.1 96L267.1 84.02C300.6 51.37 347 36.51 392.6 44.1C461.5 55.58 512 115.2 512 185.1V190.9C512 232.4 494.8 272.1 464.4 300.4L283.7 469.1C276.2 476.1 266.3 480 256 480C245.7 480 235.8 476.1 228.3 469.1L47.59 300.4C17.23 272.1 0 232.4 0 190.9V185.1C0 115.2 50.52 55.58 119.4 44.1C164.1 36.51 211.4 51.37 244 84.02L255.1 96zM255.1 141.3L221.4 106.6C196.1 81.31 160 69.77 124.7 75.66C71.21 84.58 31.1 130.9 31.1 185.1V190.9C31.1 223.6 45.55 254.7 69.42 277L250.1 445.7C251.7 447.2 253.8 448 255.1 448C258.2 448 260.3 447.2 261.9 445.7L442.6 277C466.4 254.7 480 223.6 480 190.9V185.1C480 130.9 440.8 84.58 387.3 75.66C351.1 69.77 315.9 81.31 290.6 106.6L255.1 141.3z"/></svg>',
        party: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="120"><path fill="#f40b0b" d="M150.6 201.4C144.6 195.3 136.4 192 127.9 192c-2.234 0-4.482 .2269-6.711 .6996c-10.62 2.312-19.37 9.844-23.19 20.03l-95.1 256c-4.406 11.75-1.531 25 7.344 33.88C15.47 508.7 23.66 512 32 512c3.781 0 7.594-.6563 11.25-2.031l255.1-96c10.19-3.812 17.72-12.56 20.03-23.19c2.281-10.62-.9683-21.72-8.656-29.41L150.6 201.4zM32 479.1l28.71-76.66l47.93 47.93L32 479.1zM141.5 438.9l-68.51-68.51l22.54-60.2l106.2 106.2L141.5 438.9zM234.6 404L107.9 277.3l19.98-53.26c.0293-.0078-.0254 0 0 0l160.1 159.1L234.6 404zM496 256h-10.88c-50.63 0-98.06 25.78-130.2 70.69c-5.125 7.219-3.469 17.19 3.719 22.34C361.5 351 364.8 352 368 352c4.969 0 9.906-2.344 13.03-6.688C407 308.9 444.1 288 485.1 288H496C504.8 288 512 280.8 512 272S504.8 256 496 256zM176 160C179.2 160 182.5 159 185.3 157C230.2 124.9 256 77.5 256 26.88V16c0-8.844-7.156-16-15.1-16c-8.844 0-16 7.156-16 16v10.88c0 40.16-20.88 78.09-57.31 104.1C159.5 136.1 157.8 146.1 162.1 153.3C166.1 157.7 171 160 176 160zM64 56c13.26 0 24-10.75 24-24c0-13.26-10.74-24-24-24c-13.25 0-23.1 10.74-23.1 24C40.01 45.25 50.75 56 64 56zM480 424c-13.25 0-24 10.74-24 24c0 13.25 10.75 24 24 24c13.26 0 24-10.75 24-24C504 434.7 493.3 424 480 424zM480 136c-13.25 0-24 10.74-24 24c0 13.25 10.75 24 24 24c13.26 0 24-10.75 24-24C504 146.7 493.3 136 480 136zM382.7 175.1c19.84-23.5 18.41-46 17.25-64.06c-1-16.12-1.75-27.75 9.781-41.44c11.47-13.59 22.81-14.62 38.5-16.03c17.88-1.625 40.09-3.656 60.03-27.28c5.719-6.75 4.844-16.84-1.906-22.53c-6.688-5.656-16.81-4.844-22.53 1.906c-11.47 13.59-22.81 14.62-38.5 16.03c-17.88 1.625-40.09 3.656-60.03 27.28c-19.84 23.5-18.41 46-17.25 64.06c1 16.12 1.75 27.75-9.781 41.44c-11.44 13.53-22.75 14.56-38.44 15.97c-17.88 1.594-40.09 3.594-60 27.22c-5.719 6.75-4.844 16.84 1.906 22.53C264.7 222.8 268.3 224 272 224c4.531 0 9.062-1.938 12.22-5.688c11.44-13.53 22.75-14.56 38.44-15.97C340.5 200.8 362.8 198.8 382.7 175.1z"/></svg>',
        hand_heart: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="120"><path fill="#f40b0b" d="M275.2 250.5c6.998 7.377 18.5 7.377 25.5 0l108.1-114.2c31.49-33.13 29.72-88.22-5.65-118.7c-35.37-30.63-81.38-16.91-104.9 7.724L287.1 36.91L276.8 25.28C253.9 1.149 207.6-13.45 171.1 17.56C136.7 48.06 134.7 103.1 166.4 136.3L275.2 250.5zM192.3 41.79C213.2 23.79 240.6 33.17 254.2 47.54l33.74 35.26l33.74-35.26c13.5-14.13 40.99-23.76 61.86-5.752c23.74 20.63 20.12 54.51 3.373 72.14L287.9 217.7L188.1 113.9C172.2 96.56 168.7 62.42 192.3 41.79zM558.2 333.6c-9.5-8.619-22-13.37-35.12-13.37c-12.5 0-24.88 4.248-34.62 12.12l-61.45 49.21c-1.928 1.545-4.324 2.385-6.795 2.385h-41.62c4.625-9.619 6.5-20.74 4.75-32.23C379.4 323.7 353.7 304 325.6 304H181.2c-20.75 0-40.1 6.746-57.5 19.24l-38.5 28.73H16c-8.801 0-16 7.195-16 15.99s7.198 16.04 15.1 16.04l80.01 .0091l46.86-35.29c11.12-8.244 24.62-12.74 38.37-12.74h146.7c13.25 0 24 10.74 24 23.99s-10.75 23.98-24 23.98H239.1c-8.75 0-16 7.246-16 15.99c0 8.746 7.25 15.99 16 15.99h180.2c9.625 0 19-3.248 26.62-9.246l61.62-49.22c4.25-3.373 9.5-5.248 14.62-5.248c5 0 9.875 1.75 13.75 5.248c10.12 9.119 9.375 24.48-.875 32.61l-100.7 80.58c-7.584 6.064-17.01 9.369-26.72 9.369H16c-8.801 0-15.1 7.189-15.1 15.98S7.198 512 15.1 512h392.5c17 0 33.38-5.871 46.62-16.37l100.9-80.7c12.25-9.744 19.5-24.36 19.1-39.98C576.5 359.3 569.1 344.2 558.2 333.6z"/></svg>'
      }
    },
    about () {
      return _get(this.section, 'attributes.about', {})
    },
    slides () {
      return _get(this.about, 'images', [])
    },
    clonedSlides () {
      const slides = [...this.slides]

      for (let i = 1; i <= 2; i++) {
        slides.unshift(this.slides[this.slides.length - i])
      }

      for (let i = 0; i < 2; i++) {
        slides.push(this.slides[i])
      }

      return slides
    },
    slidesStyle () {
      return {
        width: this.width * this.clonedSlides.length + 'px',
        left: -this.width * 2 + 'px',
        transform: `translateX(${this.index * -this.width + 'px'})`
      }
    },
    steps () {
      return _get(this.section, 'attributes.steps', {})
    },
    counter () {
      return _get(this.section, 'attributes.counter', {})
    },
    partners () {
      return _get(this.section, 'attributes.partners', {})
    },
    live () {
      return _get(this.section, 'attributes.live', {})
    }
  },
  methods: {
    onResize () {
      if (this.$refs.slider && this.slides.length)
        this.width = this.$refs.slider.clientWidth
    },
    toDOM (text) {
      if (!text) return text

      return text.replace(/<b>(.*?)<\/b>/g, '<strong>$1</strong> ').replace(/<red>(.*?)<\/red>/g, '<span>$1</span> ')
    },
    goToPrev () {
      if (this.index <= 0) {
        this.has_transition = false
        this.$refs.slides.style.transform = `translateX(${-this.width * this.slides.length + 'px'})`

        setTimeout(() => {
          this.has_transition = true
          this.index = this.slides.length - 1
        })
      } else {
        this.index--
      }
    },
    goToNext () {
      if (this.index >= this.slides.length - 1) {
        this.has_transition = false
        this.$refs.slides.style.transform = `translateX(${-this.width * -1 + 'px'})`

        setTimeout(() => {
          this.has_transition = true
          this.index = 0
        })
      } else {
        this.index++
      }
    },
    setCounter () {
      this.counter_date = []

      const duration = intervalToDuration({
        start: new Date(),
        end: new Date(this.counter.end_date),
      })

      Object.keys(duration).forEach(key => {
        const value = duration[key]

        if (['years', 'months'].includes(key) && !value) return

        this.counter_date.push({ label: key, value: value <= 9 ? '0' + value : value.toString() })
      })
    }
  },
  unmounted () {
    window.removeEventListener('resize', this.onResize)

    if (this.interval)
      clearInterval(this.interval)
  }
})
</script>

<style lang="scss" scoped>
$color--primary: #f40b0b;
$color--secondary: #131415;
$color--tertiary: #B2BCFF;

@keyframes animation-hero {
  from { transform: translateY(100px) rotate(10deg); }
  to { transform: translateY(0) rotate(0deg); }
}

@keyframes animation-del {
  from { width: 0; }
  to { width: 100%; }
}

@keyframes animation-arrow {
  from { width: 0; }
  to { width: 112px; }
}

@keyframes animation-title {
  from { transform: translateX(-10px); opacity: 0; padding-left: 0; }
  to { transform: translateX(0); opacity: 1; padding-left: 112px; }
}

@keyframes animation-date {
  from { transform: translateX(-100%); }
  to { transform: translateX(0%) }
}

@keyframes show-up {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

@keyframes bullet {
  from { transform: translateY(-40%); opacity: 0; }
  to { transform: translateY(-50%); opacity: 1; }
}

@keyframes icon {
  from { transform: translateX(-100vw); opacity: 1; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes icon-color {
  from { fill: $color--primary; }
  to { fill: $color--tertiary; }
}

@keyframes about-content {
  from { transform: translateX(-20%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

.wrapper {
  max-width: 1318px;
  margin: 0 auto;
  padding: 0 $padding__base * 1.5;

  @include mq(sm) {
    padding: 0 $padding__base * 2;
  }
}

:deep(.cta) {
  display: inline-block;
  color: $white;
  padding: 19px 20px 17px;
  background: $color--primary;
  line-height: 1;
  transition: all .2s;
  font-weight: 500;
  border-radius: 35px;
  transition: all .2s;
  text-align: center;
  min-width: 250px;

  @include mq(lg) {
    padding: 25px 20px 23px;
  }

  &:hover {
    background: $color--primary;
    color: $white;
  }

  &:hover {
    background: darken($color--primary, 5);
  }
}

.home {
  background: $white;
}

.hero {
  position: relative;
  color: $white;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;

  &::before {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    background: #401B1B;
    opacity: 0.65;
    z-index: 0;
  }

  &::after {
    content: '';
    display: block;
    position: absolute;
    bottom: -1px;
    left: -0.5%;
    width: 101%;
    padding-top: calc((90.952/1440) * 100%);
    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI0LjAuMywgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCAxNDQwIDkxIiBzdHlsZT0iZW5hYmxlLWJhY2tncm91bmQ6bmV3IDAgMCAxNDQwIDkxOyIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+CjxzdHlsZSB0eXBlPSJ0ZXh0L2NzcyI+Cgkuc3Qwe2ZpbGwtcnVsZTpldmVub2RkO2NsaXAtcnVsZTpldmVub2RkO2ZpbGw6I0ZGRkZGRjt9Cjwvc3R5bGU+Cjxwb2x5Z29uIGNsYXNzPSJzdDAiIHBvaW50cz0iMCw5MSAxNDQwLDkxIDE0NDAsMCAwLDc5LjIgIi8+Cjwvc3ZnPgo=');
    background-repeat: no-repeat;
    background-position: left bottom;
    background-size: 100% auto;
    z-index: 5;
    animation: animation-hero .2s ease-in-out 0s 1 normal forwards
  }

  &__wrapper {
    position: relative;
    z-index: 10;
    padding-top: 100px;
    padding-bottom: 100px;

    @include mq(sm) {
      padding-top: 155px;
      padding-bottom: 170px;
    }
  }

  &__title {
    font-size: rem(26px);
    font-weight: bold;

    @include mq(sm) {
      font-size: rem(36px);
    }

    @include mq(md) {
      font-size: rem(52px);
    }

    span {
      display: block;
      position: relative;
      opacity: 0;

      @for $i from 1 through 4 {
        :deep() &:nth-child(#{$i}) {
          animation: show-up .2s ease-in-out #{0.5 + ($i * .1)}s 1 normal forwards;

          &:not(:first-child) {
            @include mq(sm) {
              animation: animation-title .2s ease-in-out #{1.4 + ($i * 0.1)}s 1 normal forwards;

              &::before {
                animation: animation-arrow .2s ease-in-out #{1.4 + ($i * 0.1)}s 1 normal forwards
              }
            }
          }
        }
      }

      &:not(:first-child) {
        &::before {
          content: '';
          display: block;
          background-image: url('https://koalect-images.s3.eu-west-3.amazonaws.com/clients/24htelevie/animation-arrow.svg');
          background-position: left center;
          background-repeat: no-repeat;
          background-size: 89px auto;
          position: absolute;
          left: 0;
          top: 0;
          width: 0;
          height: 100%;
        }
      }
    }

    :deep() del {
      text-decoration: none;
      display: inline-block;
      position: relative;

      &::after {
        content: '';
        display: block;
        background: url('https://koalect-images.s3.eu-west-3.amazonaws.com/clients/24htelevie/animation-delete.svg');
        background-size: 236px auto;
        background-position: left center;
        background-repeat: no-repeat;
        position: absolute;
        left: 0;
        top: 0;
        width: 0;
        height: 100%;
        animation: animation-del .4s ease-in-out 0.8s 1 normal forwards
      }
    }
  }

  &__date {
    font-size: rem(15px);
    position: relative;
    display: inline-block;
    overflow: hidden;
    opacity: 0;
    animation: show-up .2s ease-in-out 1.8s 1 normal forwards;
    margin-top: $margin__base * 2;

    @include mq(sm) {
      font-size: rem(22px);
      padding-right: 55px;
    }

    &::before {
      content: '';
      display: block;
      background-image: url('https://koalect-images.s3.eu-west-3.amazonaws.com/clients/24htelevie/animation-date.svg');
      background-position: left center;
      background-size: 100% auto;
      background-repeat: no-repeat;
      position: absolute;
      top: 0;
      transform: translateX(-100%);
      width: 100%;
      height: 100%;
      animation: animation-date .2s ease-in-out 2s 1 normal forwards;
      z-index: 0;
    }

    span {
      position: relative;
      z-index: 10;
    }
  }

  :deep(.hero__fb) {
    color: $white;
    opacity: 0;
    transition: opacity .2s;
    animation: show-up .2s ease-in-out 2.2s 1 normal forwards;
    margin-bottom: $margin__base * 2;
    display: block;

    @include mq(sm) {
      margin-bottom: 0;
    }
  }

  &__row {
    margin-top: $margin__base * 3;

    @include mq(sm) {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 100px;
    }

    @include mq(md) {
      margin-top: 154px;
    }
  }

  .organisers {
    opacity: 0;
    animation: show-up .2s ease-in-out 2.3s 1 normal forwards;
    text-align: right;

    @include mq(sm) {
      text-align: left;
    }

    &__title {
      margin-bottom: $margin__base;
      font-size: rem(14px);
      font-weight: 500;

      @include mq(sm) {
        margin-bottom: $margin__base * 2;
      }
    }

    &__logos {
      display: inline-grid;
      grid-template-columns: 90px 90px;
      gap: $margin__base * 2;

      @include mq(sm) {
        grid-template-columns: 120px 120px;
      }
    }
  }

  .organiser {
    :deep(.organiser__link) {
      display: block;
      transition: all .2s;

      &:hover {
        opacity: 0.7;
      }
    }

    &__logo {
      max-width: 100%;
      height: auto;
      display: block;
    }
  }
}

.stats {
  text-align: center;
  color: $white;
  margin-top: -60px;
  z-index: 10;
  position: relative;

  @include mq(sm) {
    margin-top: -80px;
  }

  @include mq(md) {
    margin-top: -120px;
  }

  :deep() {
    .counter {
      &__ul {
        justify-content: center;
        background: none;
        padding: 0;
        margin: 0;

        @include mq(sm) {
          display: flex;
        }
      }

      &__li {
        background: $color--primary;
        border-radius: 47px;
        padding: 20px 18px;
        text-align: left;
        font-size: rem(16px);
        margin-bottom: $margin__base;
        text-align: center;

        @include mq(sm) {
          margin: 0;
          flex: 0 0 auto;
          margin: 0 32px 0 0;
          width: 220px;

          &:nth-child(3n+3), &:last-child {
            margin: 0;
          }
        }

        @include mq(md) {
          margin-right: 84px;
          padding: 43px 37px;
          width: 240px;
          font-size: rem(18px);

          &:nth-child(3n+3), &:last-child {
            margin: 0;
          }
        }
      }

      &__value {
        font-weight: 700;
        font-size: rem(24px);

        @include mq(md) {
          font-size: rem(32px);
        }
      }
    }
  }

  &[scroll-animation] {
    :deep(.counter__li) {
      opacity: 0;
    }
  }

  &[scroll-animation="true"] {
    @for $i from 1 through 3 {
      :deep(.counter__li):nth-child(#{$i}) {
        animation: show-up .2s ease-in-out #{$i * .1}s 1 normal forwards;
      }
    }
  }
}

.sports {
  padding: $margin__base * 3 0;

  @include mq(sm) {
    padding: $margin__base * 8 0;
  }

  &__mobile {
    @include mq(sm) {
      display: none;
    }

    &-item {
      text-align: center;
      position: relative;
      padding: $margin__base * 2 0;

      & :deep() svg path {
        fill: $color--tertiary;
      }

      [scroll-animation] & {
        opacity: 0;
      }

      [scroll-animation="true"] & {
        @for $i from 1 through 10 {
          :deep() &:nth-child(#{$i}) {
            animation: show-up .2s ease-in-out #{($i * .1)}s 1 normal forwards;
          }
        }
      }

      &:not(:last-child) {
        &::after {
          content: '';
          display: block;
          width: 11px;
          height: 11px;
          background: $color--primary;
          border-radius: 50%;
          position: absolute;
          bottom: -5px;
          left: 50%;
          transform: translateX(-50%);
        }
      }
    }
  }

  &__names, &__icons {
    display: none;

    @include mq(sm) {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
    }
  }

  &__name {
    position: relative;
    padding: $padding__base $padding__base * 2;

    &:not(:last-child) {
      &::after {
        content: '';
        display: block;
        width: 11px;
        height: 11px;
        background: $color--primary;
        border-radius: 50%;
        position: absolute;
        top: 50%;
        right: -5px;
        transform: translateY(-50%);
      }
    }

    [scroll-animation] & {
      opacity: 0;

      &::after {
        opacity: 0;
      }
    }

    [scroll-animation="true"] & {
      @for $i from 1 through 10 {
        :deep() &:nth-child(#{$i}) {
          animation: show-up .2s ease-in-out #{($i * .1)}s 1 normal forwards;

          &::after {
            animation: bullet .2s ease-in-out #{($i * .15)}s 1 normal forwards;
          }
        }
      }
    }
  }

  &__icons {
    margin-top: $margin__base * 2.5;
  }

  &__icon {
    padding: calc($padding__base / 1.5);

    @include mq(md) {
      padding: $padding__base $padding__base * 1.5;
    }

    @include mq(lg) {
      padding: $padding__base $padding__base * 3;
    }

    [scroll-animation] & {
      opacity: 0;
    }

    [scroll-animation="true"] & {
      $x: 10;
      @for $i from 1 through 10 {
        :deep() &:nth-child(#{$x}) {
          animation: icon 1.5s ease-in-out #{($i * .1)}s 1 normal forwards;
        }

        $x: $x - 1;
      }
    }

    svg {
      path {
        [scroll-animation="true"] & {
          $x: 10;
          @for $i from 1 through 10 {
            :deep() &:nth-child(#{$x - 1}) {
              animation: icon-color 1.4s ease-in-out #{($i * .1)}s 1 normal forwards;
            }

            $x: $x - 1;
          }
        }
      }
    }

    & :deep() svg {
      transform-origin: center;

      @include mq(sm) {
        transform: scale(0.8);
      }

      @include mq(md) {
        transform: scale(1);
      }
    }
  }
}

.about {
  &__wrapper {
    display: grid;
    grid-template-columns: 1fr;
    gap: $margin__base * 2;

    @include mq(sm) {
      grid-template-columns: 1fr 1fr;
      gap: $margin__base * 2;
    }

    @include mq(lg) {
      grid-template-columns: 1fr 513px;
    }

    @include mq(md) {
      gap: $margin__base * 4;
    }

    @include mq(lg) {
      gap: $margin__base * 7;
    }
  }

  &__content {
    font-size: rem(18px);

    @include mq(md) {
      font-size: rem(24px);
    }

    @include mq(lg) {
      font-size: rem(32px);
    }

    ul li {
      [scroll-animation] & {
        opacity: 0;
      }

      [scroll-animation="true"] & {
        @for $i from 1 through 5 {
          :deep() &:nth-child(#{$i}) {
            animation: about-content 0.2s ease-in-out #{($i * .1)}s 1 normal forwards;
          }
        }
      }
    }

    & :deep() strong {
      font-weight: 700;
    }

    & :deep() span {
      color: $color--primary;
      font-size: 2em;
    }
  }

  &__slider {
    position: relative;
    overflow: hidden;
    width: 100vw;
    left: -$padding__base * 1.5;

    [scroll-animation] & {
      opacity: 0;
    }

    [scroll-animation="true"] & {
      animation: show-up .2s ease-in-out .5s 1 normal forwards;
    }

    @include mq(sm) {
      width: 100%;
      left: 0;
    }

    &-slides {
      display: flex;
      position: relative;

      &.is-animated {
        transition: all .4s ease-in-out;
      }
    }

    &-slide {
      position: relative;
      background-color: #FAF7F8;
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
      flex: 1 1 auto;

      @include aspect-ratio(513, 580);

      svg {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        color: $lg;
      }
    }

    &-nav {
      display: flex;
      justify-content: center;
      position: relative;
      transform: translateY(-50%);

      @include mq(sm) {
        justify-content: flex-end;
      }
    }

    &-btn {
      background: $color--primary;
      transition: all .2s;

      &:hover {
        background: darken($color--primary, 5);
      }

      &--prev {
        margin-right: $margin__base * 2;
        padding: 13px 30px 13px 18px;

        &:hover {
          svg {
            transform: translateX(-10px);
          }
        }
      }

      &--next {
        padding: 13px 18px 13px 30px;

        &:hover {
          svg {
            transform: translateX(10px);
          }
        }
      }

      svg {
        width: 40px;
        height: auto;
        display: block;
        transition: all .2s;

        @include mq(sm) {
          width: 60px;
        }
      }
    }
  }
}

.steps {
  padding: $padding__base * 2 0;

  &__wrapper {
    display: grid;
    grid-template-columns: 1fr;
    gap: $margin__base * 2;
    counter-reset: steps;

    @include mq(md) {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  & [scroll-animation="true"]  {
    @for $i from 1 through 6 {
      .step :deep() &:nth-child(#{$i}) {
        animation: show-up .2s ease-in-out #{$i * .1}s 1 normal forwards;
      }
    }
  }

  &__item {
    [scroll-animation] & {
      opacity: 0;
    }

    [scroll-animation="true"] & {
      @for $i from 1 through 6 {
        :deep() &:nth-child(#{$i}) {
          animation: show-up 0.2s ease-in-out #{($i * .1)}s 1 normal forwards;
        }
      }
    }
  }

  .step {
    background: #FAF7F8;
    box-shadow: 10px 10px 10px 0 rgba(0,0,0,0.05);
    padding: $padding__base * 4 $padding__base;
    line-height: 1.75;

    @include mq(sm) {
      padding: $padding__base * 4 $padding__base * 2;
    }

    @include mq(lg) {
      padding: $padding__base * 9 $padding__base * 2;
    }

    &__title {
      color: $color--primary;
      font-weight: 500;
      font-size: rem(22px);

      &::before {
        counter-increment: steps;
        content: counter(steps) ". ";
        font-weight: 700;
        font-size: rem(32px);
      }
    }
  }

  &__footer {
    text-align: center;
    margin-top: $margin__base * 4;

    [scroll-animation] & {
      opacity: 0;
    }

    [scroll-animation="true"] & {
      animation: show-up 0.2s linear 1s 1 normal forwards;
    }
  }
}

.projects {
  padding: $margin__base * 2 0;

  &__title {
    color: $color--primary;
    font-weight: 700;
    font-size: rem(32px);
    text-align: center;

    @include mq(sm) {
      font-size: rem(38px);
    }

    [scroll-animation] & {
      opacity: 0;
    }

    [scroll-animation="true"] & {
      animation: show-up .2s ease-in-out 0s 1 normal forwards;
    }
  }

  &__list {
    .card {
      [scroll-animation] & {
        opacity: 0;
      }

      [scroll-animation="true"] & {
        @for $i from 1 through 12 {
          :deep() &:nth-child(#{$i}) {
            animation: show-up .2s ease-in-out #{$i * .1}s 1 normal forwards;
          }
        }
      }
    }
  }

  &__footer {
    text-align: center;

    [scroll-animation] & {
      opacity: 0;
    }

    [scroll-animation="true"] & {
      animation: show-up 0.2s linear 1s 1 normal forwards;
    }
  }
}

.counter {
  padding: $padding__base * 4 0;
  margin-top: $margin__base * 4;
  background: #E8EAFF;
  transition: all .2s ease-out;

  @include mq(sm) {
    margin-top: $margin__base * 6;
    padding: $padding__base * 4 0 72px;
  }

  @include mq(md) {
    margin-top: $margin__base * 8;
    padding: $padding__base * 4 0 124px;
  }

  &__wrapper {
    [scroll-animation] & {
      opacity: 0;
    }

    [scroll-animation="true"] & {
      animation: show-up 0.2s linear 0.2s 1 normal forwards;
    }
  }

  &__title {
    color: #10155E;
    font-weight: 700;
    font-size: rem(32px);
    text-align: center;

    @include mq(sm) {
      font-size: rem(38px);
    }
  }

  &__list {
    display: flex;
    justify-content: center;
    text-align: center;
  }

  &__item {
    position: relative;
    padding: 0 calc($padding__base / 2);
    font-size: rem(36px);
    color: $color--primary;
    line-height: 1;
    flex: 1 1 0px;

    [scroll-animation] & {
      opacity: 0;
    }

    [scroll-animation="true"] & {
      @for $i from 1 through 12 {
        :deep() &:nth-child(#{$i}) {
          animation: show-up .2s ease-in-out #{0.1 + $i * .1}s 1 normal forwards;
        }
      }
    }

    @include mq(sm) {
      font-size: rem(72px);
    }

    @include mq(md) {
      padding: 0 $padding__base * 2;
      font-size: rem(118px);
    }

    @include mq(lg) {
      padding: 0 45px;
      font-size: rem(118px);
    }

    &:not(:last-child) {
      &::after {
        display: block;
        content: ':';
        font-weight: 700;
        position: absolute;
        right: 0;
        top: 0;
        transform: translateX(50%);
      }
    }
  }

  &__value {
    color: $color--primary;
    font-weight: 700;
    display: block;
  }

  &__label {
    display: block;
    font-size: rem(10px);
    color: #131415;
    margin-top: $margin__base * 2;

    @include mq(sm) {
      font-size: rem(18px);
      margin-top: $margin__base * 3;
    }
  }
}

.partners {
  padding: $padding__base * 2 0;

  @include mq(sm) {
    padding: 64px 0 116px;
  }

  &__title {
    color: $color--primary;
    font-weight: 700;
    font-size: rem(32px);
    text-align: center;

    @include mq(sm) {
      font-size: rem(38px);
    }

    [scroll-animation] & {
      opacity: 0;
    }

    [scroll-animation="true"] & {
      animation: show-up .2s ease-in-out 0s 1 normal forwards;
    }
  }

  &__logos {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    max-width: 920px;
    margin: 0 auto;
  }

  &__item {
    width: 33%;
    padding: $padding__base;

    @include mq(sm) {
      width: 20%;
      padding: $padding__base $padding__base * 2;
    }

    [scroll-animation] & {
      opacity: 0;
    }

    [scroll-animation="true"] & {
      @for $i from 1 through 20 {
        :deep() &:nth-child(#{$i}) {
          animation: show-up .2s ease-in-out #{$i * .05}s 1 normal forwards;
        }
      }
    }
  }

  .partner {
    display: flex;
    align-items: center;
    justify-content: center;

    :deep(.partner__link) {
      display: block;
    }

    &__logo {
      max-width: 100%;
      height: auto;
    }
  }
}

.live {
  padding: $padding__base * 4 0;
  margin: $margin__base * 4 0;
  background: #E8EAFF;

  @include mq(sm) {
    padding: $padding__base * 4 0 72px;
  }

  @include mq(md) {
    margin: $margin__base * 4;
  }

  &[scroll-animation] {
    opacity: 0;
  }

  &[scroll-animation="true"] {
    animation: show-up .2s ease-in-out 0s 1 normal forwards;
  }

  &__title {
    color: #10155E;
    font-weight: 700;
    font-size: rem(32px);
    text-align: center;

    @include mq(sm) {
      font-size: rem(38px);
    }
  }

  &__video {
    position: relative;
    margin-top: $margin__base * 3;

    @include mq(sm) {
      margin-top: $margin__base * 6;
    }
  }
}
</style>
